import { useNavigate } from "react-router-dom";
import MenuTop from "../../../../components/menu/top/menu-top";
import Form from "../../../../components/form/form";
import InfoComponent from "../../../../components/info/info";
import { useCallback, useEffect, useState } from "react";
import { useAlert } from "../../../../components/alert/alert";
import { SEVERITY } from "../../../../shared/interfaces/alert";
import { FormProps, FormTypes } from "../../../../shared/interfaces/form";
import Icons from "../../../../components/icons/iconts";
import {
    createBeneficiaryProjectService,
    fetchBeneficiariesService,
    fetchProjectsService,
    BeneficiaryProjectData
} from "../service/service";

export function BeneficiaryProjectAdd() {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [isLoading, setIsLoading] = useState(false);
    const [beneficiaries, setBeneficiaries] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);

    // Buscar beneficiários e projetos
    const fetchSelectOptions = useCallback(async () => {
        try {
            setIsLoading(true);
            
            const [beneficiariesData, projectsData] = await Promise.all([
                fetchBeneficiariesService(),
                fetchProjectsService()
            ]);
            
            setBeneficiaries(beneficiariesData);
            setProjects(projectsData);
            
        } catch (error: any) {
            showAlert(
                error.message || 'Erro ao carregar dados para o formulário',
                SEVERITY.ERROR
            );
        } finally {
            setIsLoading(false);
        }
    }, [showAlert]);

   // Criar novo vínculo
    const handleSubmit = async (data: any) => {
        try {
            setIsLoading(true);
            
            // Validações
            if (!data.beneficiaryId) {
                throw new Error('Selecione um beneficiário');
            }
            
            if (!data.projectId) {
                throw new Error('Selecione um projeto');
            }

            if (!data.project_end_date) {
                throw new Error('Data de fim é obrigatória');
            }
            
            if (!data.monthly_cost || data.monthly_cost <= 0) {
                throw new Error('Custo mensal deve ser maior que zero');
            }
            
            // 🔥 VALIDAÇÃO DE DATA: Verificar se data de fim não é no passado
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const endDate = new Date(data.project_end_date);
            
            if (endDate < today) {
                throw new Error('Data de fim não pode ser no passado');
            }
            
            // 🔥 CORREÇÃO: Preparar dados para envio - monthly_cost em centavos
            const beneficiaryProjectData: BeneficiaryProjectData = {
                beneficiaryId: data.beneficiaryId,
                projectId: data.projectId,
                project_end_date: data.project_end_date, // 🔥 OBRIGATÓRIO: Enviar data de fim
                monthly_cost: parseFloat(data.monthly_cost.toString().replace(',', '.')) * 100, // 🔥 CORRIGIDO: Multiplicar por 100 para enviar em centavos
                description: data.description || ''
            };
            
            console.log('🔥 Dados enviados para API:', beneficiaryProjectData); // Debug
            
            await createBeneficiaryProjectService(beneficiaryProjectData);
            
            showAlert('Vínculo beneficiário-projeto criado com sucesso!', SEVERITY.SUCCESS);
            navigate('/beneficiario-projeto');
            
        } catch (error: any) {
            console.error('🔥 Erro ao criar vínculo:', error);
            showAlert(
                error.message || 'Erro ao criar vínculo beneficiário-projeto',
                SEVERITY.ERROR
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSelectOptions();
    }, [fetchSelectOptions]);

    // Configuração dos campos do formulário - SEM botão submit (Form já gerencia)
    const formFields: FormProps[] = [
        {
            name: 'beneficiaryId',
            typeOfField: FormTypes.SELECT,
            placeholder: 'Selecione um beneficiário',
            label: 'Beneficiário *',
            width: 100,
            rules: { required: 'Beneficiário é obrigatório' },
            options: {
                data: beneficiaries.map(beneficiary => ({
                    label: `${beneficiary.name} - ${beneficiary.type || 'Tipo não informado'}`,
                    value: beneficiary.id
                })),
                placeholder: 'Selecione um beneficiário'
            }
        },
        {
            name: 'projectId',
            typeOfField: FormTypes.SELECT,
            placeholder: 'Selecione um projeto',
            label: 'Projeto *',
            width: 100,
            rules: { required: 'Projeto é obrigatório' },
            options: {
                data: projects.map(project => ({
                    label: `${project.name} - ${project.principal_investigator || 'PI não informado'}`,
                    value: project.id
                })),
                placeholder: 'Selecione um projeto'
            }
        },
        {
            name: 'project_end_date',
            typeOfField: FormTypes.DATE,
            placeholder: 'Data de fim do projeto',
            label: 'Data de Fim *',
            width: 250,
            rules: { required: 'Data de fim é obrigatória' }
        },
        {
            name: 'monthly_cost',
            typeOfField: FormTypes.CURRENCY,
            placeholder: 'Custo mensal do projeto',
            label: 'Custo Mensal (R$) *',
            width: 50,
            rules: { required: 'Custo mensal é obrigatório' }
        },
        {
            name: 'description',
            typeOfField: FormTypes.TEXTAREA,
            placeholder: 'Descrição do vínculo (opcional)',
            label: 'Descrição',
            width: 100,
            minRows: 3,
            maxRows: 6
        }
    ];

    return (
        <div className="container-client">
            <MenuTop />
            
            <div className='container-client__content'>
                <div className="container-client__content__title">
                    <h1 className="container-client__content__title-text">
                        Criar Vínculo Beneficiário com o Projeto
                    </h1>
                </div>

                <div className="container-client__content__form">
                    <InfoComponent />
                    
                    {!isLoading && beneficiaries.length === 0 && (
                        <div style={{
                            padding: '16px',
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            borderRadius: '6px',
                            marginBottom: '16px',
                            border: '1px solid #ffeaa7',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <Icons iconName="Warning" fontSize={20} color="#856404" />
                            <div>
                                <strong>Atenção:</strong> Nenhum beneficiário encontrado. 
                                <a 
                                    href="/beneficiarios/adicionar" 
                                    style={{ marginLeft: '8px', color: '#0066cc' }}
                                >
                                    Criar beneficiário primeiro
                                </a>
                            </div>
                        </div>
                    )}
                    
                    {!isLoading && projects.length === 0 && (
                        <div style={{
                            padding: '16px',
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            borderRadius: '6px',
                            marginBottom: '16px',
                            border: '1px solid #ffeaa7',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <Icons iconName="Warning" fontSize={20} color="#856404" />
                            <div>
                                <strong>Atenção:</strong> Nenhum projeto encontrado.
                                <a 
                                    href="/projetos/adicionar" 
                                    style={{ marginLeft: '8px', color: '#0066cc' }}
                                >
                                    Criar projeto primeiro
                                </a>
                            </div>
                        </div>
                    )}
                    
                    <Form
                        data={formFields}
                        title="Criar Vínculo Beneficiário com o Projeto"
                        onSubmitForm={handleSubmit}
                        onCancel={() => navigate('/beneficiario-projeto/listar')}
                        isLoading={isLoading}
                        isReadOnly={isLoading || beneficiaries.length === 0 || projects.length === 0}
                    />

                </div>
            </div>
        </div>
    );
}